// Hideo - OAuth "PKCE" sign-in (Dropbox): no secret in the site. The service's sign-in page opens in a window that returns
// to oauth.html, which passes the code back to this page (BroadcastChannel 'pedly-oauth' / localStorage 'pedly_oauth':
// stored names, never rename them); the token renews itself with the refresh token.
// Ported from the legacy cloud/cloud.js (verbatim copy in tests/fixtures/legacy/cloud.js).
import { t } from '../../i18n'
import { CS, cloudErr, saveDriveState, tokenFresh } from './common'

/** Address registered with the services: oauth.html next to the app */
export const OAUTH_REDIRECT = location.origin + location.pathname.replace(/[^/]*$/, '') + 'oauth.html'
const b64url = (buf: ArrayBuffer | Uint8Array): string => btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
async function pkcePair(): Promise<{ verifier: string; challenge: string }> {
  const v = b64url(crypto.getRandomValues(new Uint8Array(32)))
  return { verifier: v, challenge: b64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(v))) }
}

interface OAuthMessage { code?: string; state?: string; error?: string }
/** Opens the service's sign-in page and waits for the code that oauth.html sends back (must be called inside a click) */
function oauthCode(url: string, state: string): Promise<string> {
  return new Promise((res, rej) => {
    let bc: BroadcastChannel | null = null, timer: ReturnType<typeof setTimeout> | null = null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const done = (fn: (v: any) => void, v: unknown) => { clearTimeout(timer as ReturnType<typeof setTimeout>); window.removeEventListener('storage', onStorage); if (bc) bc.close(); try { localStorage.removeItem('pedly_oauth') } catch { /* blocked storage */ } fn(v) }
    const handle = (m: OAuthMessage | null) => {
      if (!m || m.state !== state) return
      if (m.error) done(rej, new Error(m.error === 'access_denied' ? t('Sign-in cancelled.') : t('Sign-in failed ({error}).', { error: m.error })))
      else done(res, m.code)
    }
    const onStorage = (e: StorageEvent) => { if (e.key === 'pedly_oauth' && e.newValue) { try { handle(JSON.parse(e.newValue)) } catch { /* not our message */ } } }
    try { bc = new BroadcastChannel('pedly-oauth'); bc.onmessage = e => handle(e.data) } catch { /* no BroadcastChannel: localStorage only */ }
    window.addEventListener('storage', onStorage)
    timer = setTimeout(() => done(rej, new Error(t('Sign-in was not completed in time: please try again.'))), 300000)
    const w = window.open(url, 'pedlyOAuth', 'width=520,height=720')
    if (!w) done(rej, new Error(t('Your browser blocked the sign-in window: allow pop-up windows for this site and try again.')))
  })
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function oauthForm(url: string, params: Record<string, string>): Promise<any> {
  const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: new URLSearchParams(params) })
  const j = await r.json().catch(() => ({}))
  if (!r.ok) throw cloudErr(t('Sign-in failed ({error}).', { error: j.error_description || j.error || r.status }), r.status, JSON.stringify(j))
  return j
}

/** A service signed in with PKCE */
export interface PkceConfig {
  id: string
  key(): string
  authUrl(p: { challenge: string; state: string }): string
  tokenUrl: string
}
/** Shared PKCE token: valid → use it; expired → renew it with the refresh token; otherwise (interactive only) sign in again */
export async function pkceToken(cfg: PkceConfig, interactive: boolean): Promise<string | null> {
  const st = CS(cfg.id), a = st.auth || {}
  if (a.access && tokenFresh(a.exp as number)) return a.access
  if (a.refresh) {
    try {
      const j = await oauthForm(cfg.tokenUrl, { grant_type: 'refresh_token', refresh_token: a.refresh, client_id: cfg.key() })
      st.auth = { access: j.access_token, exp: Date.now() + (j.expires_in || 3600) * 1000, refresh: j.refresh_token || a.refresh }; saveDriveState()
      return st.auth.access as string
    } catch (e) { console.warn(e); if (!interactive) return null }
  }
  if (!interactive) return null
  const { verifier, challenge } = await pkcePair(), state = b64url(crypto.getRandomValues(new Uint8Array(16)))
  const code = await oauthCode(cfg.authUrl({ challenge, state }), state)
  const j = await oauthForm(cfg.tokenUrl, { grant_type: 'authorization_code', code, code_verifier: verifier, client_id: cfg.key(), redirect_uri: OAUTH_REDIRECT })
  st.auth = { access: j.access_token, exp: Date.now() + (j.expires_in || 3600) * 1000, refresh: j.refresh_token || '' }; saveDriveState()
  return st.auth.access as string
}
